import React, { useEffect, useRef, useState } from "react";
import { Search, ChevronDown, MoreHorizontal, Info, Check, Trash2 } from "lucide-react";
import { Avatar, Chip, Menu, MenuItem, Tooltip } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReferencedPCLsDrawer from "../components/ReferencedPCLsDrawer";
import VersionsDrawer from "../components/VersionsDrawer";
import { fetchCodeIndexes, duplicateCodeIndex, type TableRowItem } from "../services/codeIndexService";
import { fetchAllFilters, fetchBusinessUnits } from "../services/filtersService";
import CodeIndexDetailsDrawer from "../components/CodeIndexDetailsDrawer";
// TablePrimitives kept for other pages
import AvatarImage from "../components/AvatarImage";
import UserSelector from "../components/UserSelector";
import DataTable, { Column } from "../components/ui/DataTable";
import ForbiddenState from "../components/ui/ForbiddenState";
import Button from "../components/ui/Button";
import { useSecurity } from "../security/SecurityContext";
import { getCodesheetActionAccess, type CodesheetAction } from "../security/codesheetAccess";
import axios from "../axiosConfig";
import { showConfirmDeleteModal, showSuccessModal, showErrorModal } from "../components/ui/ConfirmDialog";
import { isAxiosForbidden } from "../lib/utils";
import { logSystemError } from "../services/usageService";

type CodeIndexSortBy = 'createdAt' | 'format' | 'lastModification' | 'neverUpdated';

function isNeverUpdatedRow(row: TableRowItem) {
  return row.lastModification === 'Never updated'
    || !String(row.lastModificationRaw || '').trim()
    || String(row.lastModificationRaw || '').startsWith('0001-01-01');
}

function getLastModificationTimestamp(row: TableRowItem) {
  const parsed = Date.parse(row.lastModificationRaw || '');
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortRowsByLastModification(rows: TableRowItem[], sortBy: CodeIndexSortBy, sortDir: 'asc' | 'desc') {
  if (sortBy !== 'lastModification' && sortBy !== 'neverUpdated') return rows;

  return [...rows].sort((a, b) => {
    const aNeverUpdated = isNeverUpdatedRow(a);
    const bNeverUpdated = isNeverUpdatedRow(b);

    if (sortBy === 'neverUpdated') {
      if (aNeverUpdated !== bNeverUpdated) return aNeverUpdated ? -1 : 1;
      return getLastModificationTimestamp(b) - getLastModificationTimestamp(a);
    }

    if (aNeverUpdated !== bNeverUpdated) return aNeverUpdated ? 1 : -1;

    const diff = getLastModificationTimestamp(a) - getLastModificationTimestamp(b);
    return sortDir === 'asc' ? diff : -diff;
  });
}

export default function CodeIndexPage() {
  // ---------------- State ----------------
  const [activeTab, setActiveTab] = useState("All BU's");
  const [businessUnits, setBusinessUnits] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { has, user, roles, permissions } = useSecurity();
  const canCreate = has("Create_Codesheets");

  // Data
  const [tableData, setTableData] = useState<TableRowItem[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);

  // Filters
  const [keyword, setKeyword] = useState("");
  const [pendingKeyword, setPendingKeyword] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [productFamily, setProductFamily] = useState("All");
  const [productLine, setProductLine] = useState("All");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedPCLs, setSelectedPCLs] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<CodeIndexSortBy>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Filter options (from API with dummy fallback)
  const [productFamilyOptions, setProductFamilyOptions] = useState<string[]>(["All"]);
  const [productLineOptions, setProductLineOptions] = useState<string[]>(["All"]);
  const [pclOptions, setPclOptions] = useState<string[]>([]);
  const [userOptions, setUserOptions] = useState<Array<{ name: string; emailAddress: string; avatarUri?: string | null }>>([]);
  const [filtersLoading, setFiltersLoading] = useState<boolean>(false);

  // Popovers open/close
  const [pclOpen, setPclOpen] = useState(false);

  // Details Drawer
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TableRowItem | null>(null);
  const [detailsStartInEdit, setDetailsStartInEdit] = useState<boolean>(false);
  // const [detailsImageUrl, setDetailsImageUrl] = useState<string | null>(null);
  // deprecated: handled inside DetailsDrawer
  // const [detailsTab, setDetailsTab] = useState(0);

  // Referenced PCLs Drawer
  const [refsOpen, setRefsOpen] = useState(false);
  const [refsContext, setRefsContext] = useState<{ id?: string; partNumber: string; pcl: string } | null>(null);

  // Versions Drawer
  const [versionsDrawerOpen, setVersionsDrawerOpen] = useState(false);
  const [versionsContext, setVersionsContext] = useState<{ id?: string; partNumber: string } | null>(null);

  // Create via DetailsDrawer
  const [createOpen, setCreateOpen] = useState(false);
  // const [createDefaults, setCreateDefaults] = useState<{ partNumber?: string } | null>(null);

  const pclRef = useRef<HTMLDivElement | null>(null);
  const hasHydratedFromURL = useRef(false);

  // Row actions menu (three dots)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const openMenu = Boolean(menuAnchorEl);
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, rowId: string) => {
    e.stopPropagation?.();
    setMenuAnchorEl(e.currentTarget);
    setMenuRowId(rowId);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  const getRowActionAccess = (row: TableRowItem, action: CodesheetAction) => (
    getCodesheetActionAccess({
      user,
      roles,
      permissions,
      codesheetBusinessUnit: row.businessUnit || null,
    }, action)
  );

  const openEditForRow = (row: TableRowItem) => {
    const access = getRowActionAccess(row, 'edit_codesheet');
    if (!access.allowed) return;
    setSelectedRow(row);
    setDetailsStartInEdit(true);
    setDetailsOpen(true);
  };

  const openViewForRow = (row: TableRowItem) => {
    const access = getRowActionAccess(row, 'view_codesheet');
    if (!access.allowed) return;
    setSelectedRow(row);
    setDetailsStartInEdit(false);
    setDetailsOpen(true);
  };

  const deleteRow = async (row: TableRowItem) => {
    const access = getRowActionAccess(row, 'delete_selection');
    if (!access.allowed) return;
    const confirmed = await showConfirmDeleteModal({ entityLabel: 'codesheet' });
    if (!confirmed) return;
    try {
      setLoading(true);
      await axios.delete(`/CodeIndexes/${encodeURIComponent(String(row?.id || ''))}`);
      await showSuccessModal({ title: 'Deleted', timer: 1200, showConfirmButton: false });
      await applySearch();
    } catch (_e) {
      logSystemError(user, {
        action: 'delete_codesheet',
        error: _e,
        resource: 'codesheet',
        resourceId: String(row?.id || ''),
      });
      await showErrorModal({ title: 'Error', description: 'Failed to delete' });
    } finally {
      setLoading(false);
    }
  };

  const copyCodeIndex = async (row: TableRowItem) => {
    const access = getRowActionAccess(row, 'duplicate_codesheet');
    if (!access.allowed) return;
    setLoading(true);
    try {
      const newId = await duplicateCodeIndex(String(row?.id || ''), row.format);
      if (newId) {
        setCurrentPage(1);
        if (page === 1) await applySearch();
      }
    } finally {
      setLoading(false);
    }
  };

  // Transform userOptions for UserSelector component
  const userSelectorOptions = userOptions.map(user => ({
    id: user.emailAddress,
    name: user.name,
    emailAddress: user.emailAddress,
    avatarUri: user.avatarUri || null
  }));

  // Close popovers on outside click
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (pclRef.current && !pclRef.current.contains(e.target as Node)) setPclOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openCreate = () => {
    setCreateOpen(true);
  };

  const navigateToCodeIndexDetails = (row: TableRowItem) => {
    const id = String(row?.id || '');
    if (!id) return;
    navigate(`/details/${encodeURIComponent(id)}`);
  };

  // Hydrate state from URL on first mount
  useEffect(() => {
    if (hasHydratedFromURL.current) return;
    hasHydratedFromURL.current = true;

    const kw = searchParams.get('keyword') || "";
    const pf = searchParams.get('productFamily') || "All";
    const pl = searchParams.get('productLine') || "All";
    const tab = searchParams.get('tab') || "All BU's";
    const usersParam = searchParams.get('users');
    const pclsParam = searchParams.get('pcls');

    setKeyword(kw);
    setProductFamily(pf);
    setProductLine(pl);
    setActiveTab(tab);
    if (usersParam) setSelectedUsers(usersParam.split(',').filter(Boolean));
    if (pclsParam) setSelectedPCLs(pclsParam.split(',').filter(Boolean));
  }, [searchParams]);

  // Keep URL in sync with state
  useEffect(() => {
    if (!hasHydratedFromURL.current) return;
    const params: Record<string, string> = {};
    if (keyword.trim()) params.keyword = keyword;
    if (productFamily !== 'All') params.productFamily = productFamily;
    if (productLine !== 'All') params.productLine = productLine;
    if (activeTab !== "All BU's") params.tab = activeTab;
    if (selectedPCLs.length) params.pcls = selectedPCLs.join(',');
    setSearchParams(params, { replace: true });
  }, [keyword, productFamily, productLine, activeTab, selectedPCLs, setSearchParams]);

  const tabs = ["All BU's", ...businessUnits];

  /*
   Demo (anterior):
   const demoData = [
     { partNumber: "2948375610", productLine: "AMZ", revision: "BQ", format: "364", pcl: "0581", description: "Standard unit ventilator", lastModification: "May 15, 2025 | 17:35", creator: "Sotirios", productFamily: "ATS" },
     { partNumber: "1029384756", productLine: "Maverik II", revision: "BQ", format: "245", pcl: "0581", description: "Standard unit ventilator", lastModification: "May 15, 2025 | 17:35", creator: "Victor O.", productFamily: "AAH" },
     { partNumber: "9182736450", productLine: "WME", revision: "BQ", format: "803", pcl: "0581", description: "Standard unit ventilator", lastModification: "May 15, 2025 | 17:35", creator: "Victor O.", productFamily: "AAH" },
     { partNumber: "5738291046", productLine: "AMZ", revision: "BQ", format: "308", pcl: "0581", description: "Standard unit ventilator", lastModification: "May 15, 2025 | 17:35", creator: "Marco E.", productFamily: "ATS" },
     { partNumber: "5647382910", productLine: "Coils", revision: "BQ", format: "890", pcl: "0581", description: "Standard unit ventilator", lastModification: "May 15, 2025 | 17:35", creator: "Victor O.", productFamily: "Chiller" },
     { partNumber: "1928374650", productLine: "Rooftop", revision: "BQ", format: "482", pcl: "0581", description: "Standard unit ventilator", lastModification: "May 15, 2025 | 17:35", creator: "Victor O.", productFamily: "AAH" },
     { partNumber: "3847562910", productLine: "Wall-Mount", revision: "BQ", format: "671", pcl: "0581", description: "Standard unit ventilator", lastModification: "May 15, 2025 | 17:35", creator: "Victor O.", productFamily: "ATS" },
     { partNumber: "7583926410", productLine: "AWS", revision: "BQ", format: "517", pcl: "0581", description: "Standard unit ventilator", lastModification: "May 15, 2025 | 17:35", creator: "Sotirios", productFamily: "Chiller" },
   ];
  */

  useEffect(() => {
    // Removed initial fetch; rely on reactive fetch after URL hydration
  }, []);

  // Load Business Units for tabs
  useEffect(() => {
    let isMounted = true;
    (async () => {
      const units = await fetchBusinessUnits();
      if (!isMounted) return;
      setBusinessUnits(Array.isArray(units) ? units : []);
    })();
    return () => { isMounted = false; };
  }, []);

  // ---- Fetch filter options reactively (PF/PL drive subsequent options) ----
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setFiltersLoading(true);
        const pfParam = productFamily === 'All' ? null : productFamily;
        const plParam = productLine === 'All' ? null : productLine;
        const resp = await fetchAllFilters(pfParam, plParam, null, null, null, true);
        if (!isMounted) return;
        const pfOpts = ["All", ...Array.from(new Set(resp.productFamilies))];
        const plOpts = ["All", ...Array.from(new Set(resp.productLines))];
        setProductFamilyOptions(pfOpts);
        setProductLineOptions(plOpts);
        setPclOptions(resp.pcls);
        setUserOptions(resp.users);

        // Ensure current selections are valid
        if (productFamily !== 'All' && !pfOpts.includes(productFamily)) setProductFamily('All');
        if (productLine !== 'All' && !plOpts.includes(productLine)) setProductLine('All');
        setSelectedUsers(prev => {
          const validEmails = new Set(resp.users.map(u => u.emailAddress));
          const pruned = prev.filter(e => validEmails.has(e));
          return pruned.length === prev.length ? prev : pruned;
        });
        setSelectedPCLs(prev => {
          const pruned = prev.filter(p => resp.pcls.includes(p));
          return pruned.length === prev.length ? prev : pruned;
        });
      } finally {
        if (isMounted) setFiltersLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [productFamily, productLine]);

  // ---- Server pagination ----
  const totalPages = Math.max(1, Math.ceil((totalResults || 0) / itemsPerPage));
  const page = Math.min(currentPage, totalPages);

  // pageList moved into DataTable component

  // -------------- UI helpers --------------
  const togglePCL = (p: string) => {
    setSelectedPCLs(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
    setCurrentPage(1);
  };

  const handleUserSelectionChange = (userIds: string[]) => {
    setSelectedUsers(userIds);
    setCurrentPage(1);
  };

  const openReferencedPCLs = (row: TableRowItem) => {
    setRefsContext({ id: row.id, partNumber: row.partNumber, pcl: row.pcl });
    setRefsOpen(true);
  };

  const openVersionsDrawer = (row: TableRowItem) => {
    const access = getRowActionAccess(row, 'view_version_history');
    if (!access.allowed) return;
    setVersionsContext({ id: row.id, partNumber: row.partNumber });
    setVersionsDrawerOpen(true);
  };

  const getInitials = (name: string) => {
    return name
      .split(/\s+/)
      .map(part => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  // legacy dicebear url removed; avatars resolved via AvatarImage + API

  const applySearch = async () => {
    const isLastModificationSort = sortBy === 'lastModification' || sortBy === 'neverUpdated';
    const orderByField = isLastModificationSort
      ? 'updated_at'
      : sortBy === 'format'
        ? 'format_id'
        : 'created_at';
    const requestedPage = isLastModificationSort ? 1 : page;
    const requestedPageSize = isLastModificationSort
      ? Math.max(totalResults || itemsPerPage, itemsPerPage)
      : itemsPerPage;
    const descending = sortBy === 'createdAt' ? true : sortDir === 'desc';

    try {
      setLoading(true);
      setError(null);
      setForbidden(false);
      const res = await fetchCodeIndexes({
        keyword: keyword.trim() ? keyword.trim() : null,
        productFamily: productFamily !== 'All' ? productFamily : null,
        productLine: productLine !== 'All' ? productLine : null,
        users: selectedUsers.length ? selectedUsers : null,
        pcls: selectedPCLs.length ? selectedPCLs : null,
        businessUnit: activeTab !== "All BU's" ? activeTab : null,
        page: requestedPage,
        pageSize: requestedPageSize,
        orderByField,
        descending,
      });

      let listResult = res;
      if (isLastModificationSort && res.totalResults > res.items.length) {
        listResult = await fetchCodeIndexes({
          keyword: keyword.trim() ? keyword.trim() : null,
          productFamily: productFamily !== 'All' ? productFamily : null,
          productLine: productLine !== 'All' ? productLine : null,
          users: selectedUsers.length ? selectedUsers : null,
          pcls: selectedPCLs.length ? selectedPCLs : null,
          businessUnit: activeTab !== "All BU's" ? activeTab : null,
          page: 1,
          pageSize: res.totalResults,
          orderByField,
          descending,
        });
      }

      const sortedItems = sortRowsByLastModification(listResult.items, sortBy, sortDir);
      const pageStart = (page - 1) * itemsPerPage;
      const pageEnd = pageStart + itemsPerPage;
      setTableData(isLastModificationSort ? sortedItems.slice(pageStart, pageEnd) : sortedItems);
      setTotalResults(listResult.totalResults);
    } catch (_e) {
      if (isAxiosForbidden(_e)) {
        setForbidden(true);
        setError(null);
      } else {
        logSystemError(user, {
          action: 'load_codesheet_list',
          error: _e,
          resource: 'codesheet',
          context: {
            keyword,
            productFamily,
            productLine,
            selectedUsers,
            selectedPCLs,
            businessUnit: activeTab,
            page,
            pageSize: itemsPerPage,
            orderByField,
            descending,
          },
        });
        setError('Failed to load data');
      }
      setTableData([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh table whenever filters change
  useEffect(() => {
    if (!hasHydratedFromURL.current) return;
    applySearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, productFamily, productLine, selectedUsers, selectedPCLs, activeTab, page, itemsPerPage, sortBy, sortDir]);

  // Update keyword immediately and gate: only trigger when empty or >= 2 chars
  useEffect(() => {
    const v = pendingKeyword;
    const canSearch = v.trim().length === 0 || v.trim().length >= 2;
    if (!canSearch) return;
    setIsSearching(true);
    setKeyword(v);
    setIsSearching(false);
  }, [pendingKeyword]);

  // Details open handled via edit flow or other triggers if needed

  // Image blob handling moved into DetailsDrawer

  // Inline avatars are rendered per row; demo component removed

  // Prefetch removed: avatars are now base64 data URIs handled by AvatarImage

  // -------------- Render --------------
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header / Filters - Full width */}
      <div className="bg-[#373737] text-white p-4 w-full">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-end gap-4 flex-nowrap">
            {/* Search */}
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-300">Search</label>
              <div className="relative">
                <input
                  value={pendingKeyword}
                  onChange={(e) => { setPendingKeyword(e.target.value); setCurrentPage(1); }}
                  type="text"
                  placeholder="Keywords"
                  className="bg-white text-gray-900 px-3 py-2 pr-10 rounded w-48 text-sm"
                />
                {isSearching ? (
                  <span className="loader loader--sm absolute right-2 top-2.5" />
                ) : (
                  <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>

            {/* Product Family */}
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-300">Product Family</label>
              <div className="relative">
                <select
                  className="bg-white text-gray-900 px-3 py-2 pr-8 rounded w-36 text-sm appearance-none"
                  value={productFamily}
                  onChange={(e) => { setProductFamily(e.target.value); setCurrentPage(1); }}
                  disabled={filtersLoading}
                >
                  {productFamilyOptions.map(pf => <option key={pf} value={pf}>{pf}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Product Line */}
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-300">Product Line</label>
              <div className="relative">
                <select
                  className="bg-white text-gray-900 px-3 py-2 pr-8 rounded w-40 text-sm appearance-none"
                  value={productLine}
                  onChange={(e) => { setProductLine(e.target.value); setCurrentPage(1); }}
                  disabled={filtersLoading}
                >
                  {productLineOptions.map(pl => <option key={pl} value={pl}>{pl}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Users Related */}
            <UserSelector
              label="Users Related"
              userOptions={userSelectorOptions}
              selectedUserIds={selectedUsers}
              onSelectionChange={handleUserSelectionChange}
              placeholder="Select users"
              width="w-64"
              disabled={filtersLoading}
            />

            {/* PCL (multi-select chips) */}
            <div className="flex flex-col gap-1 relative" ref={pclRef}>
              <label className="text-sm text-gray-300">PCL</label>
              <div
                onClick={() => setPclOpen(o => !o)}
                className="bg-white text-gray-900 min-h-[40px] w-48 rounded text-sm flex items-center gap-1 flex-nowrap whitespace-nowrap border border-gray-300 px-2 py-1 cursor-text overflow-x-auto overflow-y-hidden"
              >
                {selectedPCLs.length === 0 && (
                  <span className="text-gray-500">Select PCL</span>
                )}
                {selectedPCLs.map(p => (
                  <Chip
                    key={p}
                    variant="outlined"
                    size="small"
                    label={p}
                    onDelete={(e) => { e.stopPropagation?.(); togglePCL(p); }}
                    sx={{ mr: 0.5, '& .MuiChip-deleteIcon': { color: '#1D5176' }, flexShrink: 0 }}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ))}
                <ChevronDown className={`ml-auto h-4 w-4 text-gray-500 ${pclOpen ? 'rotate-180' : ''}`} />
              </div>

              {pclOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white text-gray-900 rounded shadow-lg p-2 w-48 z-50 border border-gray-200">
                  <div className="max-h-56 overflow-auto pr-1">
                    {pclOptions.map(p => (
                      <label key={p} className="flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                        <span className="text-sm">{p}</span>
                        <input
                          checked={selectedPCLs.includes(p)}
                          onChange={() => togglePCL(p)}
                          type="checkbox"
                          className="h-4 w-4"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <button onClick={() => setSelectedPCLs([])} className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1"><Trash2 className="h-3.5 w-3.5"/>Clear</button>
                    <button onClick={() => setPclOpen(false)} className="text-xs bg-gray-900 text-white px-2 py-1 rounded flex items-center gap-1"><Check className="h-3.5 w-3.5"/>Apply</button>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="ml-auto flex items-center gap-3">
              <Button
                onClick={canCreate ? openCreate : undefined}
                size="md"
                disabled={!canCreate}
                leftIcon={<img className="h-6" src="/leftIcon.svg" alt="Daikin Logo" />}
                title={canCreate ? undefined : "You don't have permission to create"}
              >
                New Code Sheet
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs - Full width */}
      <div className="bg-gray-100 w-full">
        <div className="max-w-screen-2xl mx-auto px-4">
          <div className="flex gap-8 overflow-auto">
            <div className="flex gap-8 relative">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                  className={`py-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === tab
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab}
                </button>
              ))}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-300"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Table - Limited width container, with internal scroll */}
      <div className="bg-gray-100 p-6">
        <div className="max-w-screen-2xl mx-auto">
          {error && (
            <div className="px-4 py-2 border-b border-red-200 text-sm text-red-600 bg-red-50">{error}</div>
          )}
          <DataTable<TableRowItem>
            maxBodyHeight={'calc(100vh - 240px)'}
            loading={loading}
            rows={tableData}
            page={page}
            totalPages={totalPages}
            onPageChange={(p) => setCurrentPage(p)}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            totalResults={totalResults}
            onRowClick={navigateToCodeIndexDetails}
            columns={([
              { id: 'format', header: (
                <button
                  type="button"
                  onClick={() => {
                    setCurrentPage(1);
                    if (sortBy !== 'format') {
                      setSortBy('format');
                      setSortDir('asc');
                    } else {
                      setSortDir((current) => current === 'asc' ? 'desc' : 'asc');
                    }
                  }}
                  className="inline-flex items-center justify-center gap-1 select-none"
                >
                  Format
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform ${sortBy === 'format' ? (sortDir === 'asc' ? 'rotate-180 opacity-100' : 'opacity-100') : 'opacity-40'}`}
                  />
                </button>
              ), width: '8%', align: 'center', cell: (row: TableRowItem) => (
                <button type="button" onClick={(e) => { e.stopPropagation?.(); navigateToCodeIndexDetails(row); }} className="cursor-pointer">{row.format || '—'}</button>
              ) },
              { id: 'pline', header: 'Product Line', width: '10%', align: 'left', cell: (row: TableRowItem) => (
                <button type="button" onClick={(e) => { e.stopPropagation?.(); navigateToCodeIndexDetails(row); }} className="cursor-pointer">{row.productLine || '—'}</button>
              ) },
              { id: 'version', header: 'Version', width: '7%', align: 'center', cell: (row: TableRowItem) => {
                const publishedVersion = row.publishedVersion?.semVer || '—';
                const versionsCount = Array.isArray(row.versions) ? row.versions.length : 0;
                const versionAccess = getRowActionAccess(row, 'view_version_history');
                const hasVersionInfo = Boolean(row.publishedVersion?.semVer) || versionsCount > 0;

                if (!hasVersionInfo) {
                  return (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation?.(); navigateToCodeIndexDetails(row); }}
                      className="cursor-pointer"
                    >
                      —
                    </button>
                  );
                }
                
                return (
                  <Tooltip title={versionAccess.message || 'View versions'}>
                    <span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation?.(); openVersionsDrawer(row); }} 
                        className="text-blue-600 hover:underline font-medium flex items-center justify-center gap-1.5 disabled:cursor-not-allowed disabled:no-underline disabled:text-gray-400"
                        title={versionAccess.message || 'View versions'}
                        disabled={!versionAccess.allowed}
                      >
                        <span>{publishedVersion}</span>
                        {versionsCount > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            {versionsCount}
                          </span>
                        )}
                      </button>
                    </span>
                  </Tooltip>
                );
              } },
              { id: 'pcl', header: 'PCL', width: '7%', align: 'center', cell: (row: TableRowItem) => (
                row.pcl ? (
                  <button type="button" onClick={(e) => { e.stopPropagation?.(); openReferencedPCLs(row); }} className="text-blue-600 hover:underline" title="Show referenced PCLs">{row.pcl}</button>
                ) : (
                  <button type="button" onClick={(e) => { e.stopPropagation?.(); navigateToCodeIndexDetails(row); }} className="cursor-pointer">—</button>
                )
              ) },
              { id: 'desc', header: 'Description', width: '21%', align: 'left', cell: (row: TableRowItem) => (
                <button type="button" onClick={(e) => { e.stopPropagation?.(); navigateToCodeIndexDetails(row); }} className="cursor-pointer max-w-[28ch] overflow-hidden text-ellipsis whitespace-nowrap" title={row.description}>{row.description || '—'}</button>
              ) },
              { id: 'lastmod', header: (
                <button
                  onClick={() => {
                    setCurrentPage(1);
                    if (sortBy !== 'lastModification') {
                      setSortBy('lastModification');
                      setSortDir('desc');
                    } else if (sortDir === 'desc') {
                      setSortDir('asc');
                    } else {
                      setSortBy('neverUpdated');
                    }
                  }}
                  className="flex items-center gap-1 select-none"
                >
                  Last Modification
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform ${sortBy === 'lastModification' || sortBy === 'neverUpdated' ? (sortBy === 'lastModification' && sortDir === 'asc' ? 'rotate-180 opacity-100' : 'opacity-100') : 'opacity-40'}`}
                  />
                </button>
              ), width: '12%', align: 'left', cell: (row: TableRowItem) => (
                <button type="button" onClick={(e) => { e.stopPropagation?.(); navigateToCodeIndexDetails(row); }} className="cursor-pointer">{row.lastModification}</button>
              ) },
              { id: 'creator', header: 'Creator', width: '7%', align: 'left', cell: (row: TableRowItem) => row.creator || 'Unknown' },
              { id: 'stack', header: 'Stack', width: '5%', align: 'center', cell: (row: TableRowItem) => (
                Array.isArray(row.stackedUsers) && row.stackedUsers.length > 0 ? (
                  <div className="flex -space-x-2">
                    {row.stackedUsers.slice(0, 4).map((m, idx) => (
                      <div key={idx} title={m?.name || ''} className="rounded-full border-2 border-white" style={{ width: 32, height: 32 }}>
                        <AvatarImage name={m?.name || ''} filename={m?.avatarUri || null} size={32} />
                      </div>
                    ))}
                    {row.stackedUsers.length > 4 && (
                      <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-gray-700 text-xs font-semibold" title={`${row.stackedUsers.length - 4} more`}>
                        +{row.stackedUsers.length - 4}
                      </div>
                    )}
                  </div>
                ) : Array.isArray(row.stackMembers) && row.stackMembers.length > 0 ? (
                  <div className="flex -space-x-2">
                    {row.stackMembers.slice(0, 4).map((name, idx) => (
                      <Avatar key={idx} title={name} sx={{ width: 32, height: 32, fontSize: 12, border: '2px solid white', bgcolor: '#1D5176', fontWeight: 'bold' }}>
                        {getInitials(name)}
                      </Avatar>
                    ))}
                    {row.stackMembers.length > 4 && (
                      <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-gray-700 text-xs font-semibold" title={`${row.stackMembers.length - 4} more`}>
                        +{row.stackMembers.length - 4}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-500">—</span>
                )
              ) },
              { id: 'actions', header: 'Actions', width: '4%', align: 'center', cell: (row: TableRowItem) => (
                <div className="flex items-center gap-2 justify-center">
                  <Info className="h-4 w-4 text-gray-400" />
                  <button onClick={(e) => handleMenuOpen(e, String(row?.id || ''))} className="p-1 rounded hover:bg-gray-100" aria-label="Row actions">
                    <MoreHorizontal className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              ) },
            ]) as unknown as Column<TableRowItem>[]}
            emptyRender={forbidden ? (
              <ForbiddenState />
            ) : undefined}
          />
          <Menu
            anchorEl={menuAnchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuRowId && (() => {
              const row = tableData.find(r => String(r.id || '') === menuRowId);
              if (!row) return null;

              const viewAccess = getRowActionAccess(row, 'view_codesheet');
              const editAccess = getRowActionAccess(row, 'edit_codesheet');
              const deleteAccess = getRowActionAccess(row, 'delete_selection');
              const duplicateAccess = getRowActionAccess(row, 'duplicate_codesheet');

              return (
                <>
                  <Tooltip title={viewAccess.message || ''}>
                    <span>
                      <MenuItem
                        disabled={!viewAccess.allowed}
                        onClick={() => {
                          if (!viewAccess.allowed) return;
                          handleMenuClose();
                          openViewForRow(row);
                        }}
                      >
                        View
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip title={editAccess.message || ''}>
                    <span>
                      <MenuItem
                        disabled={!editAccess.allowed}
                        onClick={() => {
                          if (!editAccess.allowed) return;
                          handleMenuClose();
                          openEditForRow(row);
                        }}
                      >
                        Edit
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip title={deleteAccess.message || ''}>
                    <span>
                      <MenuItem
                        disabled={!deleteAccess.allowed}
                        onClick={async () => {
                          if (!deleteAccess.allowed) return;
                          handleMenuClose();
                          await deleteRow(row);
                        }}
                      >
                        Delete
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip title={duplicateAccess.message || ''}>
                    <span>
                      <MenuItem
                        disabled={!duplicateAccess.allowed}
                        onClick={async () => {
                          if (!duplicateAccess.allowed) return;
                          handleMenuClose();
                          await copyCodeIndex(row);
                        }}
                      >
                        Duplicate
                      </MenuItem>
                    </span>
                  </Tooltip>
                </>
              );
            })()}
          </Menu>
        </div>
      </div>

      {/* Details Drawer */}
      <CodeIndexDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        onSaved={() => { applySearch(); }}
        recordId={selectedRow?.id}
        startInEditMode={detailsStartInEdit}
        onOpenConfigurator={() => navigate(`/configurator/${selectedRow?.partNumber}`)}
      />

      {/* Referenced PCLs Drawer */}
      <ReferencedPCLsDrawer
        open={refsOpen}
        codeIndexId={refsContext?.id}
        partNumber={refsContext?.partNumber}
        pcl={refsContext?.pcl}
        onClose={() => setRefsOpen(false)}
        onSelectPCL={(pclId) => navigate(`/configurator/${refsContext?.partNumber}?pcl=${encodeURIComponent(pclId)}`)}
      />

      {/* Versions Drawer */}
      <VersionsDrawer
        open={versionsDrawerOpen}
        onClose={() => setVersionsDrawerOpen(false)}
        entityId={versionsContext?.id}
        entityType="codesheet"
        rootPath={versionsContext?.partNumber}
      />

      {/* Create CodeSheet via DetailsDrawer */}
      <CodeIndexDetailsDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        isCreate
        onSaved={() => {
          setCreateOpen(false);
          setCurrentPage(1);
          if (page === 1) applySearch();
        }}
        onOpenConfigurator={() => {}}
      />
    </div>
  );
}

// PageBtn removed (replaced with new paginator buttons styles)

// DetailsPanel removed in favor of dedicated component
